// 改成按需导入，减少打包体积
import { getInternalActionKeysByIds, InternalActionKeyId, type InternalActionKeyDef, isDeviceFunctionActionKey } from '@/define/internal_action_keys'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import firmware from '@/upgrade/firmware'
import { type VK } from '@hid/define/define'
import { deviceManager } from '@hid/device/device_manager'
import { isElectron } from '@libs/utils/envUtils'
import { DriverService } from './driver/driver'
import { DriverSocketMsgType } from '@libs/types/message'
import { DeviceManager, DeviceProtocol } from './device'
import { FnLayerCapability } from './fn_layer_capability'
import { BaseDriver } from '@hid/driver/device'
import { compare } from 'compare-versions'
import { DevicePid } from '@/define/define'

const huochairenOnboardActionIds = new Set<VK>([
  InternalActionKeyId.deviceSwitchToProfile1,
  InternalActionKeyId.deviceSwitchToProfile2,
  InternalActionKeyId.deviceSwitchToProfile3,
  InternalActionKeyId.deviceSwitchToProfile4,
  InternalActionKeyId.deviceCycleProfiles,
  InternalActionKeyId.deviceOpenWebDriver,
] as VK[])

const shouldDisableDeviceFunctionForStableAlignment = (): boolean => {
  return specialCheckCurrent(speciaBehiver.DevicesOfSelfDev) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMagnet)
}

const isHuochairenOnboardConfigSupportedSync = (): boolean => {
  if (!specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
    return true
  }

  const fwVersion = firmware.firmwareVersion.value
  return fwVersion != null && compare(fwVersion, '1.20', '>=')
}

const getSupportedDeviceFunctionActionIds = (): VK[] => {
  if (FnLayerCapability.isStableOnlyDevice() || shouldDisableDeviceFunctionForStableAlignment()) {
    return []
  }

  try {
    const ids = [...deviceManager.deviceDriver.getSupportedDeviceFunctionKeys()] as VK[]
    if (isHuochairenOnboardConfigSupportedSync()) {
      return ids
    }

    return ids.filter((id) => !huochairenOnboardActionIds.has(id))
  } catch {
    return []
  }
}

const isRoYuanMg68Max = (): boolean => {
  const currentPid = DeviceManager.instance.state.currentPid
  return currentPid === DevicePid.IROKMg68Max || currentPid === DevicePid.IROKMg68Max_24g
}

export class DeviceCapability {
  static getActionSettingDeviceFunctionActionKeys(): InternalActionKeyDef[] {
    return this.getSupportedDeviceFunctionActionKeys()
  }

  static isFnLayerSupported(): boolean {
    return FnLayerCapability.isSupported()
  }

  static isFnLayerEditable(): boolean {
    return FnLayerCapability.isEditable()
  }

  static getFnDeviceReadbackMode(): 'layout-overlay' | 'keymap-overlay' | 'none' {
    return FnLayerCapability.getDeviceReadbackMode()
  }

  static supportsFnDeviceReadback(): boolean {
    return this.getFnDeviceReadbackMode() !== 'none'
  }

  static getNormalLayerReadbackMode(): 'layout-overlay' | 'none' {
    try {
      return deviceManager.deviceDriver.getKeyLayoutOverlay !== BaseDriver.prototype.getKeyLayoutOverlay ? 'layout-overlay' : 'none'
    } catch {
      return 'none'
    }
  }

  static supportsNormalLayerReadback(): boolean {
    return this.getNormalLayerReadbackMode() !== 'none'
  }

  static supportsFnFactoryReset(): boolean {
    return FnLayerCapability.supportsFactoryReset()
  }

  static getSupportedDeviceFunctionActionKeys(): InternalActionKeyDef[] {
    return getInternalActionKeysByIds(getSupportedDeviceFunctionActionIds())
  }

  static isDeviceFunctionSupported(): boolean {
    return this.getActionSettingDeviceFunctionActionKeys().length > 0
  }

  static isImageLightUploadSupported(): boolean {
    const currentPid = DeviceManager.instance.state.currentPid
    return currentPid !== 0 && specialCheckCurrent(speciaBehiver.SupportCustomImageUpload)
  }

  static isOnboardConfigSupported(): boolean {
    if (DeviceManager.instance.state.driverProtocol !== DeviceProtocol.hid_web) {
      return false
    }

    if (!isHuochairenOnboardConfigSupportedSync()) {
      return false
    }

    try {
      return deviceManager.deviceDriver.supportsOnboardConfigs()
    } catch {
      return false
    }
  }

  static isSupportedDeviceFunctionActionKey(id: number): boolean {
    const actionId = id as VK
    return isDeviceFunctionActionKey(actionId) && getSupportedDeviceFunctionActionIds().includes(actionId)
  }

  static async isRSSupported(): Promise<boolean> {
    return await firmware.versionIs20241125a()
  }

  static async isSupportKME(): Promise<boolean> {
    const fwVersionIs103 = await firmware.fwVersionGreaterThanOrEqual('1.0.3', firmware.fwVersionDvs1)
    const fwVersionIs100 = await firmware.fwVersionGreaterThanOrEqual('1.0.0', firmware.fwVersionDvs2)
    const fwVersion20250326 = await firmware.versionIs20250326()
    if (fwVersionIs103 || fwVersionIs100 || fwVersion20250326) {
      return true
    }
    if (specialCheckCurrent(speciaBehiver.SupportKME)) {
      return true
    }
    return false
  }
  /**
   * 判断火柴人设备是否满足音乐律动要求的固件版本 (>= 1.06)
   * 如果不是火柴人设备，则默认通过检查
   */
  static async isHuoChaiRenMusicSupported(): Promise<boolean> {
    // 如果不是火柴人系列设备，直接返回 true（不拦截）
    if (!specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
      return true
    }
    // 火柴人方案需要检查固件版本
    return await firmware.isHuoChaiRenV106()
  }

  /**
   * 检查音乐律动是否可用
   * 同时检查设备硬件支持及服务连接状态
   */
  static async isMusicRhythmAvailable(): Promise<boolean> {
    // 0 如果是exe 适配的设备，都支持音乐律动功能
    if (DeviceManager.instance.state.driverProtocol == DeviceProtocol.dll_exe) {
      return true
    }
    // 1. 基础条件检查：是否在 Electron 环境且配置支持音乐律动
    const isBasicSupport = isElectron() && specialCheckCurrent(speciaBehiver.SupportMusicRhythmSupported)
    if (!isBasicSupport) return false

    // 2. 特殊设备（火柴人）固件版本检查
    if (!(await this.isHuoChaiRenMusicSupported())) return false

    // 3. 通信层检查：请求驱动服务确认律动状态
    return await DriverService.instance.askType<boolean>(DriverSocketMsgType.MTMusicalRhythmEnable)
  }

  static isKMEModeSupported = async () => {
    if (isRoYuanMg68Max()) {
      return await firmware.versionRoYuanV307()
    }

    if (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2) || specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
      return true
    } else {
      const v1 = await firmware.versionIs20241125a()
      const v2 = await firmware.versionIs20250326()
      return v1 || v2
    }
  }
}
